/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { AnyAction } from 'redux';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    SET_DEFAULT_MENU_LANGUAGE_STATE,
    SET_MENU_LANGUAGE_IS_ACTIVE,
    SET_MENU_LANGUAGE_VERTICAL_POSITION,
} from './menulanguage.types';
import { DeviceModel } from '../../../core/device/DeviceModel';
import { Storage } from '../../../core/storage/Storage';
import { showSettingsScreen } from '../../screens/screens.reducers';
import {
    setDefaultMenuLanguageStateAction,
    setIsMenuLanguageActiveAction,
    setMenuLangaugeVerticalPositionAction,
} from './menulanguage.actions';
import { SUPPORTED_LOCALES } from '../../../config';
import { setFocusedItemIndexAction, setIsMenuFocusedAction } from '../settings.actions';

const TAG: string = 'MenuLanguage';

const initialState: {
    verticalPosition: number;
    verticalLimit: number;
    isActive: boolean;
} = {
    verticalPosition: 0,
    verticalLimit: 0,
    isActive: false,
};

const menulanguageReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_MENU_LANGUAGE_VERTICAL_POSITION:
            return {
                ...state,
                verticalPosition: payload.verticalPosition,
            };
        case SET_MENU_LANGUAGE_IS_ACTIVE:
            return {
                ...state,
                isActive: payload.isActive,
            };
        case SET_DEFAULT_MENU_LANGUAGE_STATE:
            return {
                ...state,
                verticalLimit: payload.verticalLimit,
                verticalPosition: payload.verticalPosition,
            };
        default:
            return state;
    }
};

export const onKeyUpMenuLangaugeOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition } = getState().menulanguage;
    if (verticalPosition > 0) {
        const newVerticalPosition = verticalPosition - 1;
        dispatch(setMenuLangaugeVerticalPositionAction(newVerticalPosition));
        dispatch(setFocusedItemIndexAction(newVerticalPosition));
        dispatch(setIsMenuFocusedAction(true));
    }
};

export const onKeyDownMenuLanguageOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, verticalLimit } = getState().menulanguage;
    const newVerticalPosition = verticalPosition + 1;
    if (newVerticalPosition < verticalLimit) {
        dispatch(setMenuLangaugeVerticalPositionAction(newVerticalPosition));
        dispatch(setFocusedItemIndexAction(newVerticalPosition));
        dispatch(setIsMenuFocusedAction(true));
    }
};

export const showMenuLanguage = () => (dispatch: Function) => {
    dispatch(setIsMenuLanguageActiveAction(true));
};

export const hideMenuLanguage = () => (dispatch: Function) => {
    dispatch(setIsMenuLanguageActiveAction(false));
};

export const onKeyBackMenuLanguageSettings = () => (dispatch: Function) => {
    dispatch(showSettingsScreen());
    dispatch(setIsMenuLanguageActiveAction(false));
};

export const onKeyOkMenuLanguageOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition } = getState().menulanguage;
    const currentAppLanguage = DeviceModel.getTVProperties().getLang();

    if (SUPPORTED_LOCALES[verticalPosition].language === currentAppLanguage) return;

    Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
        Storage.Keys.LANGUAGE,
        SUPPORTED_LOCALES[verticalPosition].language,
    );
    Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
        Storage.Keys.LOCALE,
        SUPPORTED_LOCALES[verticalPosition].locale,
    );

    const newLang = SUPPORTED_LOCALES[verticalPosition].language;

    // TODO: We need to see how we can log this event after the app reload.
    Diagnostics.info(TAG, `Language changed to ${newLang}.`);

    DeviceModel.restartApplication();
};

export const onMouseClickMenuLanguageOperations =
    (focusItemIndex: number) => (dispatch: Function) => {
        dispatch(setMenuLangaugeVerticalPositionAction(focusItemIndex));
        dispatch(onKeyOkMenuLanguageOperations());
    };

export const setDefaultMenuLangaugeState =
    (limit: number, position: number) => (dispatch: Function) => {
        dispatch(setDefaultMenuLanguageStateAction(limit, position));
    };

export default menulanguageReducer;
